import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { api, globalErrorHandler } from '../../api/create-api.js';
import { makeCall } from '../../auth-utils.js';
import { Field, SearchBar, ExpandableBox } from '@galop/component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faEdit, faPlus, faExclamationCircle, faTimes, faSortAmountDownAlt, faSortAmountDown } from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Button, Alert } from 'reactstrap';
import { View } from './View.js';
import { Summary } from './Summary.js';
import { GetOptionText } from '../../Helpers/Helper.js';
import { AssetInfo } from '../Assets/AssetInfo.js';
import { Counts } from './Counts.js';
import Lock, { AppIsLocked, LockDate } from '../Shared/Lock.js';

export const List = (props) => {
    const { addToast } = useToasts();
    const ls = require('local-storage');
    const { userId, questions, policies, editAsset, setEditAsset, editing, setEditing } = props;
    const [assets, setAssets] = useState(props.assets || []);
    const [selectedAsset, setSelectedAsset] = useState(props.selectedAsset || ls('selected-asset'));
    const [resources, setResources] = useState([]);
    const [filteredResources, setFilteredResources] = useState([]);
    const [newResource, setNewResource] = useState(false);
    const [editResources, setEditResources] = useState({});
    const [showDetails, setShowDetails] = useState({});
    const notDirty = { all: false, leaseDetails: [] };
    const [dirtyResources, setDirtyResources] = useState(notDirty);
    const [lists, setLists] = useState({});
    const [showHeaders, setShowHeaders] = useState(false);
    //const [showCompleted, setShowCompleted] = useState(false);
    const [assetInfo, setAssetInfo] = useState(null);
    const [assetNeedsInfo, setAssetNeedsInfo] = useState(null);
    const [filtered, setFiltered] = useState('');
    const [countKey, setCountKey] = useState(null);
    const [showLockedAlert, setShowLockedAlert] = useState(AppIsLocked() && !(!!ls('alert-closed')));
    const [sortDirection, setSortDirection] = useState(ls('sort-direction') || "asc");
    const [sortField, setSortField] = useState(ls('sort-field') || "residentName");

    useEffect(() => getLists(), [selectedAsset]);

    //useEffect(() => setEditAsset(props.editAsset), [props.editAsset]);

    useEffect(() => {
        if (!AppIsLocked()) { ls.remove('alert-closed'); }
    }, []);

    useEffect(() => {
        if (!props.selectedAsset) {
            getAssets((assets) => {
                if ((assets && assets.length > 0) && ((!(ls('selected-asset')) || ls('selected-asset') === {}) || !assets.map(asset => asset.id).includes((selectedAsset || {}).id))) {
                    setSelectedAsset(assets[0]);
                }
            });
        }
        else {
            setSelectedAsset(props.selectedAsset);
        }
        getAssetInfo();
    }, [userId, props.selectedAsset]);

    useEffect(() => {
        filterResources(resources);
    }, [filtered, resources, sortField, sortDirection]);

    useEffect(() => {
        setEditAsset(false);
        setResources([]);
        setCountKey(null);
        setAssetNeedsInfo(null);
        setAssetInfo(null);
        ls('selected-asset', selectedAsset);
    }, [selectedAsset]);

    useEffect(() => {
        getAssetNeedsInfo(() => getResources());
    }, [userId, selectedAsset, editAsset])

    const getLists = () => {
        if (selectedAsset) {
            makeCall(async (authHeader) => {
                const result = await api(`api/leases/lists/${selectedAsset.id}`, { headers: authHeader })
                    .catch(error => globalErrorHandler(error, addToast));

                if (result && result.data) {
                    setLists(result.data);
                }
            });
        }
    };

    const getResources = (callback) => {
        if (userId && selectedAsset) {
            makeCall(async (authHeader) => {
                const result = await api(`api/leases/${userId}/${selectedAsset.id}?showDeleted=${!!props.showDeleted}`, { headers: authHeader })
                    .catch(error => globalErrorHandler(error, addToast));

                if (result) {
                    if (result.status === 204) {
                        setResources(null);
                    }
                    else if (result.data) {
                        setResources(result.data);
                        setCountKey(Math.random());

                        if (callback && typeof callback === 'function') {
                            callback(result.data);
                        }
                    }
                }
            });
        }
    };

    const filterResources = (unfiltered) => {
        let filteredList = [];

        if (filtered !== "") {
            filteredList = unfiltered.filter(resource => {
                const lcFilter = filtered.toLowerCase();

                // Search by table columns
                const name = (resource.residentName || '').toLowerCase(); // Name
                const unit = (resource.unit || '').toLowerCase(); // Unit
                const leaseId = (resource.leaseId || '').toString().toLowerCase(); // Lease Id
                const filingDate = (resource.filingDate ? new Date(resource.filingDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '').toString(); // Filing Date
                const completionDate = (resource.completionDate ? new Date(resource.completionDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '').toString(); // Filing Date
                const reason = (resource.reasonId && lists.reasons && lists.reasons.length > 0 ? GetOptionText(lists.reasons, resource.reasonId, 'id', 'name') : '').toLowerCase();
                const status = (resource.evictionStatusId && lists.evictionStatuses && lists.evictionStatuses.length > 0 ? GetOptionText(lists.evictionStatuses, resource.evictionStatusId, 'id', 'name') : '').toLowerCase();


                return name.includes(lcFilter)
                    || unit.includes(lcFilter)
                    || leaseId.includes(lcFilter)
                    || filingDate.includes(lcFilter)
                    || completionDate.includes(lcFilter)
                    || reason.includes(lcFilter)
                    || status.includes(lcFilter);
            });
        }
        else {
            filteredList = unfiltered;
        }

        setFilteredResources([...ApplySort(filteredList || [])]);
    };

    const ApplySort = (unsorted) => {
        return unsorted.sort((a, b) => {
            let sortA = a[sortField] || '';
            let sortB = b[sortField] || '';

            switch (sortField) {
                case "evictionStatus":
                    sortA = GetOptionText(lists.evictionStatuses, a.evictionStatusId, 'id', 'name') || '';
                    sortB = GetOptionText(lists.evictionStatuses, b.evictionStatusId, 'id', 'name') || '';
                    break;
                case "hasERA":
                    sortA = a.eraPrograms && a.eraPrograms.filter(program => !!program.eraStatusId).length > 0;
                    sortB = b.eraPrograms && b.eraPrograms.filter(program => !!program.eraStatusId).length > 0;
                    break;
                default:
                    break;
            }

            if (sortDirection === 'desc') {
                const tempSort = sortA;
                sortA = sortB;
                sortB = tempSort;
            }

            if (sortA < sortB) {
                return -1;
            }
            if (sortA > sortB) {
                return 1;
            }

            return 0;
        });
    };

    const getAssetNeedsInfo = (callback) => {
        if (selectedAsset) {
            makeCall(async (authHeader) => {
                const result = await api(`api/assets/info/${selectedAsset.id}`, { headers: authHeader })
                    .catch(error => globalErrorHandler(error, addToast));

                if (result) {
                    setAssetNeedsInfo(result.data);
                    if (result.data) {
                        //setEditAsset(true);
                        setResources(null);
                    }


                    if (callback && typeof callback === 'function') {
                        callback(result.data);
                    }
                    if (result.status === 204) {
                        //setAssetInfo(null);
                    }
                    else if (result.data) {
                        //setAssetInfo(result.data);
                        //setEditAsset(true);
                        //setResources(null);
                    }
                }
            });
        }
    };

    const getAssetInfo = () => {
        if (props.selectedAsset) {
            makeCall(async (authHeader) => {
                const result = await api(`api/assets/details/${props.selectedAsset.id}`, { headers: authHeader })
                    .catch(error => globalErrorHandler(error, addToast));

                if (result && result.data) {
                    setAssetInfo(result.data);
                }
            });
        }
    };

    const getAssets = (callback) => {
        if (!assets || assets.length === 0) {
            if (userId) {
                makeCall(async (authHeader) => {
                    const result = await api(`api/assets/${userId}`, { headers: authHeader })
                        .catch(error => globalErrorHandler(error, addToast));

                    if (result) {
                        if (result.status === 204) {
                            setAssets(null);
                        }
                        else if (result.data) {
                            setAssets(result.data);
                            if (typeof callback === 'function') {
                                callback(result.data);
                            }
                        }
                    }
                });
            }
        }
        else if (typeof callback === 'function') {
            callback(assets);
        }
    };

    const ChangeProperty = (value) => {
        if (assets && assets.length > 0) {
            const selected = assets.filter(asset => asset.id === value)[0];
            if (selected) {
                setSelectedAsset(selected);
            }
        }
    };

    const reloadResource = (id, callback) => {
        makeCall(async (authHeader) => {
            const result = await api(`api/leases/details/${id}`, { headers: authHeader })
                .catch(error => globalErrorHandler(error, addToast));

            if (result && result.data) {
                /* Handle splicing the Lease Detail record */
                const resourceObject = resources.find(resource => resource.id === id);
                const index = resources.indexOf(resourceObject);
                const newResources = resources;

                newResources[index] = result.data;
                setResources([...newResources]);
                setCountKey(Math.random());
                //filterEvictions(Search, [...newEvictions]);
            }

            if (callback && typeof callback === 'function') {
                callback(result.data);
            }
        });
    };

    const closeAlert = () => {
        setShowLockedAlert(false);
        ls('alert-closed', true);
    };

    const SortHeader = (props) => {
        const { children, className, field } = props;
        const ls = require('local-storage');
        const directions = ["asc", "desc"];
        const [sortDirection, setSortDirection] = useState(props.SortDirection || ls('sort-direction'));
        const [sortField, setSortField] = useState(props.SortField || ls('sort-field'));

        useEffect(() => props.SetSort(sortField === field ? field : null, sortDirection), [sortField, sortDirection]);

        const sortByField = () => {
            ls('sort-field', field);
            if (sortField === field) {
                const newDirection = directions.find(dir => dir !== sortDirection);
                ls('sort-direction', newDirection);
                setSortDirection(newDirection);
            }
            else {
                setSortField(field);
                setSortDirection(directions[0]);
                ls('sort-direction', directions[0]);
            }
        };

        return (<div className={`${className} cursor-pointer`} onClick={sortByField}>
            {children} {sortField === field && <small className="text-secondary"><FontAwesomeIcon icon={!(!!sortDirection) || sortDirection === directions[0] ? faSortAmountDownAlt : faSortAmountDown} /></small>}
        </div>);
    };

    const setSort = (field, direction) => {
        if (!!field) {
            setSortField(field);
            setSortDirection(direction);
        }
    };

    return (
        <div className={`${props.className} d-flex flex-column mb-5 mb-sm-0 h-100`}>
            {!props.selectedAsset &&
                <Row>
                    <Col>
                        {!assets &&
                            <div className="text-dark font-italic">There are no assets assigned to you.</div>
                        }
                    </Col>
                </Row>
            }
            <Lock
                locked={() =>
                    <Alert color="secondary" fade={false} isOpen={showLockedAlert} toggle={closeAlert}>
                        <div className="d-flex">
                            <div className="flex-fill">
                                The system has been locked as of {LockDate()}. You can view, but not update the data.
                                </div>
                            <div className="text-dark font-italic" onClick={closeAlert}>
                                Click to dismiss this alert.
                                </div>
                        </div>
                    </Alert>
                }
            />
            {!editAsset && resources && !!resources.find(resource => !!resource.eraPrograms.find(p => ((lists.eraPrograms || []).find(era => era.id === p.eraProgramId) || {}).deleted)) &&
                <Alert color="warning" className="d-flex align-items-center" fade={false}>
                    <div>
                        <FontAwesomeIcon size="2x" className="text-warning" icon={faExclamationCircle} />
                    </div>
                    <div className="flex-fill ml-2">A recently deleted ERA program is being used in a lease detail record below.</div>
                </Alert>
            }
            {!(editAsset || assetNeedsInfo) ? (selectedAsset &&
                <div className="h-75 flex-fill d-flex flex-column">
                    <Row>
                        <Col>
                            <h4>Lease Detail Records
                                <Lock
                                    unlocked={() => <Button color="link" className="text-success" disabled={newResource || editing} onClick={() => setNewResource(!newResource)}><FontAwesomeIcon icon={faPlus} /> New</Button>}
                                />
                            </h4>
                        </Col>
                        <Col sm="6" md="4">
                            <SearchBar value={filtered} onChange={(value) => setFiltered(value)} placeHolder={`Search Lease Detail Records`} Reset={() => setFiltered('')} />
                        </Col>
                    </Row>
                    {resources &&
                        <div>
                            <div className={`px-3 ${showHeaders ? 'd-block' : 'd-none d-lg-block'}`}>
                                <Row className="align-items-center">
                                    <Col md="6" lg="4">
                                        <SortHeader field="residentName" SortField={sortField} SortDirection={sortDirection} SetSort={setSort}>Resident</SortHeader>
                                    </Col>
                                    <Col sm="2" lg="1" className="px-3 px-sm-0">
                                        <SortHeader field="residentStatus" SortField={sortField} SortDirection={sortDirection} SetSort={setSort}>Status</SortHeader>
                                    </Col>
                                    <Col>
                                        <SortHeader field="evictionStatus" SortField={sortField} SortDirection={sortDirection} SetSort={setSort}>Eviction</SortHeader>
                                    </Col>
                                    <Col sm="2" lg="1" className="px-3 px-md-0">
                                        <SortHeader field="hasERA" SortField={sortField} SortDirection={sortDirection} SetSort={setSort}>ERA</SortHeader>
                                    </Col>
                                    <Col>
                                        <SortHeader field="declarationStatusId" SortField={sortField} SortDirection={sortDirection} SetSort={setSort}>CDC</SortHeader>
                                    </Col>
                                    <Col sm="3" md="12" lg="2" className="d-none d-sm-block text-right">
                                        &nbsp;
                                </Col>
                                </Row>
                            </div>
                            <div className={`d-block d-lg-none text-center cursor-pointer`} /* pt-1 mt-1 border-top*/ onClick={() => setShowHeaders(!showHeaders)}>
                                <div className="position-relative border-top border-secondary mt-1" style={{ marginBottom: -12, zIndex: -1 }}></div><span className="bg-white px-1"><FontAwesomeIcon icon={showHeaders ? faCaretUp : faCaretDown} /> Headers</span>
                            </div>
                        </div>
                    }
                    <div className={`border rounded scrollable-list mb-5 mb-sm-0 flex-fill`} style={{ overflow: 'scroll' }}>
                        <div className="p-1 pr-2 pt-2">
                            {newResource &&
                                <View className={`rounded p-2 border shadow mb-3`}
                                    {...lists}
                                    {...{ newResource, selectedAsset }}
                                    ReloadResource={getResources}
                                    MakeDirty={(dirtyReno) => {
                                        setDirtyResources({ ...dirtyResources, all: true });
                                        setEditing(true);
                                    }}
                                    EditResource={true}
                                    Details={true}
                                    Close={() => setNewResource(false)}
                                    HideDetails={() => {
                                        setNewResource(false);
                                        getResources();
                                        setEditing(false);
                                    }}
                                />
                            }
                            {resources && filteredResources ?
                                (filteredResources.map((resource, index) => {
                                    return ((editResources.all || showDetails[resource.id]) ?
                                        <View key={resource.id} className={`rounded p-2 border shadow mb-3`}
                                            {...lists}
                                            {...{ resource, selectedAsset }}
                                            ReloadResource={getResources}
                                            MakeDirty={(dirtyReno) => {
                                                setDirtyResources({ ...dirtyResources, all: true });
                                                setEditing(true);
                                            }}
                                            EditResource={editResources.all || editResources[resource.id]}
                                            Details={resource.evictions && resource.evictions.length > 0 && !editResources.all && (showDetails[resource.id] && !editResources[resource.id])}
                                            HideDetails={() => {
                                                setShowDetails({ ...showDetails, [resource.id]: !showDetails[resource.id] });
                                                setEditResources({ ...editResources, [resource.id]: !editResources[resource.id] });
                                                setEditing(false);
                                            }}
                                        />
                                        : <Summary
                                            key={resource.id || Math.random()}
                                            {...lists}
                                            {...{ resource }}
                                            showBackground={index % 2}
                                            Details={showDetails[resource.id]}
                                            ShowDetails={() => {
                                                setShowDetails({ ...showDetails, [resource.id]: !showDetails[resource.id] })
                                                setEditResources({ ...editResources, [resource.id]: !editResources[resource.id] })
                                            }}
                                            ReloadResources={() => getResources()}
                                        />);
                                })
                                )
                                :
                                (!newResource && <div className="font-italic">{selectedAsset.name} <span className="text-dark">currently has no Lease Detail records.</span></div>)
                            }
                            {resources && resources.length === 0 &&
                                <div className="font-italic"><span className="text-dark">Loading lease detail records for </span> {selectedAsset.name}<span className="text-dark">...</span></div>
                            }
                        </div>
                    </div>
                </div>
            )
                :
                <AssetInfo {...{ selectedAsset, assetInfo, setAssetInfo, editAsset, questions, policies, setEditing }} className="" Close={() => {
                    setEditAsset(false);
                    setEditing(false);
                    getAssetNeedsInfo();
                    getLists();
                }} />
            }
            {resources && countKey &&
                <Counts key={countKey} className="mt-2" {...{ selectedAsset }} />
            }
        </div>
    );
};


// WEBPACK FOOTER //
// ./src/components/LeaseDetails/List.js